import React, { useEffect, useRef, useState } from "react";
import {
	Typography,
	message,
	Spin,
	Form,
	Input,
	Button,
	notification,
	Result,
	Modal,
} from "antd";
import { useParams, useHistory } from "react-router-dom";
import planoContratualService from "services/PlanoContratualService";
import usuarioService from "services/UsuarioService";
import cupomService from "services/CupomService";
import CheckoutPagamento from "components/organism/CheckoutPagamento";
import crmService from "services/CrmService";
import InputCep, { cepValidator } from "components/atom/InputCep";
import { PUBLIC_PREFIX_PATH } from "configs/AppConfig";
import {
	cupomSeAplicaAoPlano,
	formatarPorcentagemCupom,
	aplicarCupomNoValorFixo,
} from "utils/cupom";

const { Title, Text } = Typography;

const CheckoutDireto = () => {
	const { planoId } = useParams();
	const history = useHistory();

	const [loading, setLoading] = useState(true);
	const [plano, setPlano] = useState(null);
	const [planoAtual, setPlanoAtual] = useState(null);
	const [userData, setUserData] = useState(null);
	const [taxas, setTaxas] = useState([]);
	const [qtdAlunos, setQtdAlunos] = useState(0);
	const [cupomAtivo, setCupomAtivo] = useState(null);
	const [aplicandoCupom, setAplicandoCupom] = useState(false);

	const [etapa, setEtapa] = useState("loading"); // loading, endereco, checkout, sucesso, erro
	const [checkoutError, setCheckoutError] = useState("");
	const [checkoutResult, setCheckoutResult] = useState(null);
	const [contratoLoading, setContratoLoading] = useState(false);
	const [pixCobrancaId, setPixCobrancaId] = useState(null);
	// Conta inadimplente/bloqueada NÃO faz o checkout do anual aqui: mostramos só
	// um modal instruindo a regularizar a situação na própria conta antes (a
	// migração pro anual exige assinatura ATIVA). Ver carregarDados / handleCheckoutSubmit.
	const [modalInadimplente, setModalInadimplente] = useState(false);
	const [addressLoading, setAddressLoading] = useState(false);
	const [addressForm] = Form.useForm();

	useEffect(() => {
		carregarDados();
	}, []);

	// Dispara InitiateCheckout no Meta Pixel uma vez, quando o usuário de fato
	// chega ao formulário de pagamento (etapa 'checkout'). Valor é o final
	// pós-cupom — sem isso o evento iria com o valor cheio do plano e bagunçaria
	// ROI/otimização. Conta inadimplente/bloqueada fica em etapa 'bloqueado' (só
	// vê o modal, não há checkout) e NÃO deve gerar evento de conversão falso.
	const initiateCheckoutFiredRef = useRef(false);
	useEffect(() => {
		if (
			!plano ||
			initiateCheckoutFiredRef.current ||
			etapa !== "checkout"
		)
			return;
		initiateCheckoutFiredRef.current = true;
		try {
			if (typeof window !== "undefined" && window.fbq) {
				const valorFinal = aplicarCupomNoValorFixo(plano, cupomAtivo);
				window.fbq("track", "InitiateCheckout", {
					value: Number(valorFinal) || 0,
					currency: "BRL",
					content_name: plano.nome || "Plano",
					content_ids: [String(plano.id)],
					content_type: "product",
				});
			}
		} catch {}
	}, [plano, cupomAtivo, etapa]);

	// Poll PIX status
	useEffect(() => {
		if (!pixCobrancaId) return;
		const interval = setInterval(async () => {
			try {
				const status =
					await planoContratualService.verificarStatusCobranca(
						pixCobrancaId,
					);
				if (status?.status === "CONFIRMED" || status?.status === "RECEIVED") {
					clearInterval(interval);
					setPixCobrancaId(null);
					setEtapa("sucesso");
				}
			} catch (e) {
				console.error(e);
			}
		}, 5000);
		return () => clearInterval(interval);
	}, [pixCobrancaId]);

	const carregarDados = async () => {
		try {
			// Registrar que o lead acessou o link de checkout (para comissão do vendedor)
			crmService.registrarAcessoCheckout().catch(() => {});

			const [planosRes, planoAtualRes, userRes, taxasRes, cupomRes] =
				await Promise.all([
					planoContratualService.getPlanosDisponiveis(),
					planoContratualService.getPlanoAtual(),
					usuarioService.obterPropriosDados(),
					planoContratualService.getTaxasParcelamentoCartao(),
					cupomService.obterMeuCupom().catch(() => null),
				]);

			const planoEncontrado = (planosRes || []).find(
				(p) => p.id === Number(planoId),
			);

			if (!planoEncontrado) {
				setEtapa("erro");
				setLoading(false);
				return;
			}

			setPlano(planoEncontrado);
			setPlanoAtual(planoAtualRes);
			setUserData(userRes);
			setTaxas(taxasRes || []);
			setCupomAtivo(cupomRes || null);
			if (planoAtualRes?.quantidadeAlunos) {
				setQtdAlunos(planoAtualRes.quantidadeAlunos);
			}

			// Conta inadimplente/bloqueada: a migração pro anual exige assinatura
			// ATIVA. Em vez de tentar regularizar aqui dentro, mostramos só um modal
			// pedindo pra regularizar a situação na própria conta primeiro. Não
			// renderizamos o formulário de pagamento nesse caso.
			if (
				planoAtualRes?.motor === "v2" &&
				(planoAtualRes?.estado === "inadimplente" ||
					planoAtualRes?.estado === "bloqueada")
			) {
				setModalInadimplente(true);
				setEtapa("bloqueado");
				setLoading(false);
				return;
			}

			// Verificar se precisa de endereço
			const cep = (userRes?.cep || "").toString().trim();
			const logradouro = (userRes?.logradouro || "").trim();
			const numero = (userRes?.numero_endereco || "").toString().trim();
			const bairro = (userRes?.bairro || "").trim();

			if (!cep || !logradouro || !numero || !bairro) {
				addressForm.setFieldsValue({
					cep: userRes?.cep || "",
					logradouro: userRes?.logradouro || "",
					numero_endereco: userRes?.numero_endereco || "",
					complemento_endereco: userRes?.complemento_endereco || "",
					bairro: userRes?.bairro || "",
				});
				setEtapa("endereco");
			} else {
				setEtapa("checkout");
			}
		} catch (e) {
			console.error(e);
			message.error("Erro ao carregar dados");
			setEtapa("erro");
		}
		setLoading(false);
	};

	const buscarCep = async (cep) => {
		if (!cep || cep.replace(/\D/g, "").length !== 8) return;
		try {
			const response = await fetch(
				`https://viacep.com.br/ws/${cep.replace(/\D/g, "")}/json/`,
			);
			const data = await response.json();
			if (!data.erro) {
				addressForm.setFieldsValue({
					logradouro: data.logradouro,
					bairro: data.bairro,
				});
			}
		} catch (e) {
			console.error(e);
		}
	};

	const confirmarEndereco = async (values) => {
		try {
			setAddressLoading(true);
			const requests = [
				usuarioService.atualizarEndereco({
					logradouro: values.logradouro,
					numero_endereco: values.numero_endereco,
					complemento_endereco: values.complemento_endereco,
					bairro: values.bairro,
					cep: values.cep,
				}),
			];
			if (values.cpf) {
				requests.push(
					usuarioService.atualizarPropriosDados({ cpf: values.cpf }),
				);
			}
			await Promise.all(requests);
			setUserData((prev) => ({ ...prev, ...values }));
			setEtapa("checkout");
		} catch (e) {
			message.error(
				e?.response?.data?.message || "Erro ao salvar endereço",
			);
		} finally {
			setAddressLoading(false);
		}
	};

	// Cupom aplicável (só pro banner promocional acima do checkout). O desconto
	// em si é aplicado DENTRO do CheckoutPagamento (FASE 2): o pai passa os valores
	// BASE e as props de cupom, e o componente risca o fixo e manda o cupomId.
	const cupomAplicavel = cupomSeAplicaAoPlano(plano, cupomAtivo);

	// Valores BASE (sem cupom) — o CheckoutPagamento aplica o desconto internamente.
	const valorFixoBase = parseFloat(plano?.valor_fixo || 0);
	const freqPlano = plano?.frequencia_pagamento_meses ?? 1;
	const valorVariavelMensal =
		parseFloat(plano?.valor_variavel_aluno || 0) * qtdAlunos;
	// FASE 3: no plano recorrente longo (freq > 1, ex.: anual) a variável é cobrada
	// MENSALMENTE à parte — NÃO entra no total à vista (aparece só na linha "/mês" do
	// resumo). No mensal (freq === 1) a variável compõe o total do mês, como hoje.
	const totalBase =
		freqPlano > 1 ? valorFixoBase : valorFixoBase + valorVariavelMensal;

	// Aplica o cupom digitado no checkout: resgata pra conta e recarrega o cupom
	// ativo (os descontos por plano já vêm calculados do backend). Espelha o
	// handleAplicarCupom do assinatura/index.js.
	const handleAplicarCupom = async (codigoArg) => {
		const codigo = (codigoArg || "").trim();
		if (!codigo) {
			message.warning("Digite o código do cupom.");
			return;
		}
		setAplicandoCupom(true);
		try {
			await cupomService.resgatarCupom({
				codigo,
				origem: "checkout_direto",
			});
			const novo = await cupomService.obterMeuCupom().catch(() => null);
			setCupomAtivo(novo || null);
			message.success("Cupom aplicado! O desconto já está no valor.");
		} catch (err) {
			const msg =
				err?.response?.data?.message || "Cupom inválido ou expirado.";
			message.error(msg);
		} finally {
			setAplicandoCupom(false);
		}
	};

	// "remover" no checkout: tira o cupom deste pagamento (não envia cupomId). O
	// resgate continua na conta — é só opt-out do checkout.
	const handleRemoverCupom = () => {
		setCupomAtivo(null);
	};

	// Trata a resposta padrão dos endpoints v2 ({ status: PAID | PIX | DONE } /
	// { scheduled }). PIX → polling; PAID/DONE/scheduled → sucesso. Espelha o
	// tratamento do assinatura/index.js.
	const tratarRespostaCheckout = (resp) => {
		if (resp?.scheduled) {
			setEtapa("sucesso");
			return;
		}
		setCheckoutResult(resp);
		if (resp?.status === "PIX" && resp?.cobranca?.id) {
			setPixCobrancaId(resp.cobranca.id);
			return;
		}
		if (resp?.status === "PAID" || resp?.status === "DONE") {
			notification.success({
				message: "Pagamento confirmado",
				description: "Sua assinatura foi ativada com sucesso.",
			});
			setEtapa("sucesso");
			return;
		}
		// Resposta inesperada (sem scheduled/PIX/PAID/DONE): não deixa o usuário
		// sem feedback nenhum.
		setCheckoutError(
			"Não foi possível concluir o checkout. Atualize a página e tente novamente.",
		);
	};

	// FASE 1: árvore de decisão do checkout. Em vez de sempre contratar via v1,
	// roteia pelo plano-atual (motor/estado/coorte) e pelo plano escolhido. Todos
	// os métodos v2 já existem no PlanoContratualService — nada de endpoint novo.
	const handleCheckoutSubmit = async (payload) => {
		try {
			setContratoLoading(true);
			setCheckoutError("");

			const motor = planoAtual?.motor;
			const estado = planoAtual?.estado;
			const coorte = planoAtual?.coorte;
			const freqEscolhido = plano?.frequencia_pagamento_meses ?? 1;

			// Motor v1 legado (conjunto vazio hoje): mantém o checkout v1 puro.
			if (motor !== "v2") {
				const resp =
					await planoContratualService.checkoutContratarPlano(
						plano.id,
						payload,
						null,
					);
				tratarRespostaCheckout(resp);
				return;
			}

			// GAP (Opção A): conta inadimplente/bloqueada NÃO migra pro anual aqui
			// (a migração exige assinatura ATIVA). O formulário nem chega a ser
			// renderizado nesse caso (ver carregarDados) — esta é só uma guarda
			// defensiva: reabre o modal pedindo pra regularizar na própria conta.
			if (estado === "inadimplente" || estado === "bloqueada") {
				setModalInadimplente(true);
				return;
			}

			// Ativa + mesmo plano anual escolhido: adianta +1 ano (não troca de
			// plano; sem idPlanoDestino).
			if (
				estado === "ativa" &&
				planoAtual?.id_plano_atual === plano.id &&
				freqEscolhido === 12
			) {
				const resp = await planoContratualService.adiantarAnoV2(payload);
				tratarRespostaCheckout(resp);
				return;
			}

			// LEGADO (grátis ou pago), sem oferta de funil: assina/troca pelo
			// catálogo via checkout-assinar-plano-v2 (charge-then-switch v2-native).
			// Gateado por coorte ANTES de qualquer ramo por ciclo: espelha a
			// referência assinatura/index.js (ehLegadoV2 → checkoutAssinarPlanoV2).
			// prepararAssinarPlanoLegado ativa (legado_grátis, pago_ate NULL) ou
			// anexa +1 ciclo do destino (legado_pago) cobrando só o fixo pós-cupom,
			// variável zerada. É o caminho do público do checkout direto (ELEVAR_NIVEL).
			// (Troca-ciclo com abatimento de resíduo — trocarPlanoV2 — é reservada ao
			// fluxo de flip, não à escolha de catálogo; decisão de resíduo em aberto.)
			const ehLegado =
				!planoAtual?.id_oferta &&
				(coorte === "legado_gratis" || coorte === "legado_pago");
			if (ehLegado || estado === "ativa") {
				const resp =
					await planoContratualService.checkoutAssinarPlanoV2({
						...payload,
						idPlanoDestino: plano.id,
					});
				tratarRespostaCheckout(resp);
				return;
			}

			// Fallback defensivo (estado inesperado sem coorte legado): mantém o
			// checkout v1 puro, como antes da migração.
			const resp = await planoContratualService.checkoutContratarPlano(
				plano.id,
				payload,
				null,
			);
			tratarRespostaCheckout(resp);
		} catch (error) {
			const descricaoErro =
				error.response?.data?.message ||
				error.response?.data?.errors?.[0]?.description ||
				error.response?.data?.error?.message ||
				error.message;
			setCheckoutError(
				descricaoErro ||
					"Não foi possível processar o pagamento. Verifique os dados e tente novamente.",
			);
		} finally {
			setContratoLoading(false);
		}
	};

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: 80 }}>
				<Spin size="large" />
			</div>
		);
	}

	if (etapa === "erro") {
		return (
			<Result
				status="error"
				title="Plano não encontrado"
				subTitle="O link que você acessou não corresponde a um plano disponível."
				extra={
					<Button type="primary" onClick={() => history.push("/app")}>
						Ir para o início
					</Button>
				}
			/>
		);
	}

	if (etapa === "sucesso") {
		return (
			<Result
				status="success"
				title="Assinatura realizada com sucesso!"
				subTitle={`Seu plano "${plano?.nome}" foi ativado. Você já pode usar todas as funcionalidades.`}
				extra={
					<Button type="primary" onClick={() => history.push("/app")}>
						Acessar a plataforma
					</Button>
				}
			/>
		);
	}

	return (
		<div>
			<div style={{ textAlign: "center", marginBottom: 32 }}>
				<img
					src={`${PUBLIC_PREFIX_PATH}/logo-comprido.png`}
					alt="treino.io"
					style={{ height: 40, marginBottom: 16 }}
				/>
				<Title level={3} style={{ margin: 0 }}>
					Assinar {plano?.nome}
				</Title>
				{plano?.descricao && (
					<Text type="secondary">{plano.descricao}</Text>
				)}
			</div>

			{/* Conta inadimplente/bloqueada: só um modal pedindo pra regularizar
			    na própria conta antes de migrar pro anual. O formulário de
			    pagamento não é renderizado (etapa "bloqueado"). */}
			<Modal
				open={modalInadimplente}
				title="Regularize sua assinatura primeiro"
				closable={false}
				maskClosable={false}
				keyboard={false}
				okText="Ir para minha conta"
				cancelButtonProps={{ style: { display: "none" } }}
				onOk={() => history.push("/app/assinatura")}
			>
				<p>
					Sua assinatura está com uma{" "}
					<strong>situação pendente (fatura em aberto)</strong>. Para
					migrar para o plano anual, você precisa primeiro entrar na sua
					conta e regularizar a situação. Assim que a assinatura voltar a
					ficar ativa, você poderá contratar o plano anual por aqui
					normalmente.
				</p>
			</Modal>

			{etapa === "bloqueado" && (
				<div
					style={{
						maxWidth: 500,
						margin: "0 auto",
						textAlign: "center",
					}}
				>
					<Text type="secondary">
						Regularize sua assinatura na sua conta para continuar.
					</Text>
				</div>
			)}

			{etapa === "endereco" && (
				<div style={{ maxWidth: 500, margin: "0 auto" }}>
					<Title level={5}>
						Antes de continuar, confirme seus dados
					</Title>
					<Form
						form={addressForm}
						layout="vertical"
						onFinish={confirmarEndereco}
					>
						<Form.Item
							name="cep"
							label="CEP"
							rules={[
								{ required: true, message: "CEP é obrigatório" },
								{ validator: cepValidator },
							]}
						>
							<InputCep onBlur={(e) => buscarCep(e.target.value)} />
						</Form.Item>
						<Form.Item
							name="logradouro"
							label="Logradouro"
							rules={[{ required: true, message: "Obrigatório" }]}
						>
							<Input />
						</Form.Item>
						<Form.Item
							name="numero_endereco"
							label="Número"
							rules={[{ required: true, message: "Obrigatório" }]}
						>
							<Input />
						</Form.Item>
						<Form.Item name="complemento_endereco" label="Complemento">
							<Input />
						</Form.Item>
						<Form.Item
							name="bairro"
							label="Bairro"
							rules={[{ required: true, message: "Obrigatório" }]}
						>
							<Input />
						</Form.Item>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
								loading={addressLoading}
								block
								size="large"
							>
								Continuar para o pagamento
							</Button>
						</Form.Item>
					</Form>
				</div>
			)}

			{etapa === "checkout" && plano && cupomAplicavel && (
				<div
					style={{
						background:
							"linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
						border: "1px solid #ff8034",
						borderRadius: 12,
						padding: "12px 16px",
						maxWidth: 500,
						margin: "0 auto 16px",
						textAlign: "center",
					}}
				>
					<Text style={{ color: "#c2410c", fontWeight: 700 }}>
						🎟️ Cupom {cupomAtivo?.codigo} aplicado:{" "}
						{formatarPorcentagemCupom(
							cupomAtivo?.porcentagem_desconto,
						)}{" "}
						off no plano anual.
					</Text>
				</div>
			)}

			{etapa === "checkout" && plano && (
				<CheckoutPagamento
					planName={plano.nome}
					features={[]}
					// Inputs de telefone/celular com bandeira de país (intl-tel-input),
					// idem CheckoutOferta e assinatura. O CheckoutPagamento já tem o
					// componente embutido; só é habilitado quando esta prop é passada.
					telefoneInternacional
					// FASE 2: cupom viaja pelo componente — passamos os valores BASE
					// (sem cupom) e as props de cupom; o CheckoutPagamento risca o
					// fixo, mostra o desconto e envia o cupomId no payload.
					permiteCupom
					cupomAtivo={cupomAtivo}
					planoParaCupom={plano}
					onAplicarCupom={handleAplicarCupom}
					onRemoverCupom={handleRemoverCupom}
					aplicandoCupom={aplicandoCupom}
					fixedValue={valorFixoBase}
					// No plano recorrente longo (freq > 1, ex.: anual) esta tela só
					// estende o período pagando o FIXO. A variável (1,99/aluno) já
					// faz parte da assinatura e é cobrada no ciclo mensal normal —
					// então NÃO aparece aqui (nem no total, nem como linha "/mês"),
					// pra não parecer cobrança a mais. No mensal (freq === 1) a
					// variável compõe o total do mês, como hoje.
					variableMonthlyValue={
						freqPlano > 1 ? 0 : valorVariavelMensal
					}
					studentsCount={freqPlano > 1 ? 0 : qtdAlunos}
					totalValue={totalBase}
					variavelNoTotal={freqPlano <= 1}
					maxInstallments={plano.frequencia_pagamento_meses ?? 1}
					taxasParcelamentoCartao={taxas}
					loading={contratoLoading}
					errorMessage={checkoutError}
					result={checkoutResult}
					onSubmit={handleCheckoutSubmit}
					submitLabel="CONFIRMAR ASSINATURA"
					initialHolderEmail={userData?.email || ""}
					initialHolderCpfCnpj={userData?.cpf || ""}
					initialHolderPostalCode={userData?.cep || ""}
					initialHolderAddressNumber={userData?.numero_endereco || ""}
					initialHolderAddressComplement={
						userData?.complemento_endereco || ""
					}
					initialHolderPhone={
						userData?.telefone || userData?.telefone_celular || ""
					}
					initialHolderMobilePhone={userData?.telefone_celular || ""}
				/>
			)}
		</div>
	);
};

export default CheckoutDireto;
